/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  PersistStorage,
  StateStorage,
  createJSONStorage as zustandCreateJSONStorage,
} from 'zustand/middleware';
import { createDebouncedStorage } from './debounced-storage';

type DebouncedStorageOptions = {
  /**
   * Debounce delay in milliseconds
   * @default 1000
   */
  delay?: number;
  /**
   * Maximum wait time before forcing a write (in ms)
   * @default 5000
   */
  maxWait?: number;
  /**
   * Disable debouncing (useful for development/testing)
   * @default false
   */
  disabled?: boolean;
};

/**
 * Enhanced createJSONStorage with built-in debouncing
 *
 * Drop-in replacement for Zustand's createJSONStorage that automatically
 * debounces storage writes to reduce expensive operations:
 * - Encryption/decryption via web worker
 * - postMessage calls
 * - IndexedDB/LocalStorage writes
 *
 * @example
 * ```ts
 * // Before:
 * storage: createJSONStorage(() => indexedDBStorage({ ... }))
 *
 * // After (automatic debouncing):
 * storage: createDebouncedJSONStorage(() => indexedDBStorage({ ... }))
 *
 * // With custom options:
 * storage: createDebouncedJSONStorage(
 *   () => indexedDBStorage({ ... }),
 *   { delay: 2000, maxWait: 10000 }
 * )
 * ```
 */
export function createDebouncedJSONStorage<S>(
  getStorage: () => StateStorage,
  options: DebouncedStorageOptions = {}
): PersistStorage<S> | undefined {
  const { delay = 1000, maxWait = 5000, disabled = false } = options;

  return zustandCreateJSONStorage<S>(() => {
    const storage = getStorage();

    // If disabled (e.g., in development), return raw storage
    if (disabled) {
      return storage;
    }

    // Wrap with debouncing
    return createDebouncedStorage(storage, { delay, maxWait });
  });
}

/**
 * Conditional debounced storage based on environment
 *
 * @example
 * ```ts
 * storage: createConditionalDebouncedJSONStorage(
 *   () => indexedDBStorage({ ... }),
 *   config.IS_DEVELOPMENT_ENV, // disable in dev
 *   { delay: 1000, maxWait: 5000 }
 * )
 * ```
 */
export function createConditionalDebouncedJSONStorage<S>(
  getStorage: () => StateStorage,
  shouldDisable: boolean,
  options: DebouncedStorageOptions = {}
): PersistStorage<S> | undefined {
  return createDebouncedJSONStorage<S>(getStorage, {
    ...options,
    disabled: shouldDisable,
  });
}
